export interface Line {
    height: number; // 0-1
    timeSeconds: number; // in seconds
}

export interface WavAudio {
    blob: Blob; // WAV format, 24000Hz, 1 channel
    durationSeconds: number;
    maxAmplitude: number; // 0 to 1
    getWaveform: (steps: number) => Promise<Line[]>; // steps = number of lines
}

export interface Recorder {
    stopRecording: () => Promise<WavAudio>; // WAV format, 24000Hz, 1 channel
}

const getWaveform = async (
    steps: number,
    audio: Blob,
    durationSeconds: number,
    maxAmplitude: number
): Promise<Line[]> => {
    const arrayBuffer = await audio.arrayBuffer();
    const audioContext = new OfflineAudioContext(1, 24000 * durationSeconds, 24000);
    const decodedAudio = await audioContext.decodeAudioData(arrayBuffer);
    const channelData = decodedAudio.getChannelData(0); // mono

    const samplesPerStep = Math.floor(channelData.length / steps);
    const lines: Line[] = [];

    for (let i = 0; i < steps; i++) {
        const start = i * samplesPerStep;
        const end = Math.min(start + samplesPerStep, channelData.length);
        let peak = 0;

        for (let j = start; j < end; j++) {
            const absSample = Math.abs(channelData[j]);
            if (absSample > peak) peak = absSample;
        }

        const normalizedHeight = Math.min(peak / maxAmplitude, 1);
        const time = (i / steps) * durationSeconds;

        lines.push({ height: normalizedHeight, timeSeconds: time });
    }

    return lines;
};

export const createRecorder = async (): Promise<Recorder> => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true }).catch(() => {
        throw new Error('Microphone access denied or not available.');
    });

    const audioContext = new AudioContext(); // Use default input sample rate
    const sampleRate = audioContext.sampleRate;

    const processorUrl = '/audio/recorder-processor.js'; // ✅ Your hosted processor path
    await audioContext.audioWorklet.addModule(processorUrl);

    const source = audioContext.createMediaStreamSource(stream);
    const workletNode = new AudioWorkletNode(audioContext, 'recorder-processor');

    const recordedChunks: Float32Array[] = [];

    workletNode.port.onmessage = (event) => {
        recordedChunks.push(new Float32Array(event.data));
    };

    source.connect(workletNode);
    workletNode.connect(audioContext.destination); // required to keep node alive

    const stopRecording = async (): Promise<WavAudio> => {
        workletNode.disconnect();
        source.disconnect();
        await audioContext.close();

        // 🔁 Concatenate all chunks
        const totalLength = recordedChunks.reduce((sum, chunk) => sum + chunk.length, 0);
        const buffer = new Float32Array(totalLength);
        let offset = 0;
        for (const chunk of recordedChunks) {
            buffer.set(chunk, offset);
            offset += chunk.length;
        }

        // ⏬ Resample to 24000 Hz
        const resampled = await resampleBuffer(buffer, sampleRate, 24000);

        const blob = encodeWAV(resampled, 24000, 1);
        const durationSeconds = resampled.length / 24000;
        // const maxAmplitude = Math.max(...resampled.map(Math.abs));
        const maxAmplitude = 1;

        const waveformInner = (steps: number) => getWaveform(steps, blob, durationSeconds, maxAmplitude);

        return { blob, durationSeconds, maxAmplitude, getWaveform: waveformInner };
    };

    return { stopRecording };
};

async function resampleBuffer(buffer: Float32Array, fromRate: number, toRate: number): Promise<Float32Array> {
    if (fromRate === toRate) return buffer;

    const length = Math.round(buffer.length * toRate / fromRate);
    const offlineCtx = new OfflineAudioContext(1, length, toRate);

    const sourceBuffer = offlineCtx.createBuffer(1, buffer.length, fromRate);
    sourceBuffer.copyToChannel(buffer, 0);

    const source = offlineCtx.createBufferSource();
    source.buffer = sourceBuffer;
    source.connect(offlineCtx.destination);
    source.start();

    const rendered = await offlineCtx.startRendering();
    return rendered.getChannelData(0);
}

function encodeWAV(samples: Float32Array, sampleRate: number, numChannels: number): Blob {
    const buffer = new ArrayBuffer(44 + samples.length * 2);
    const view = new DataView(buffer);

    const writeString = (offset: number, str: string) => {
        for (let i = 0; i < str.length; i++) {
            view.setUint8(offset + i, str.charCodeAt(i));
        }
    };

    const floatTo16BitPCM = (output: DataView, offset: number, input: Float32Array) => {
        for (let i = 0; i < input.length; i++, offset += 2) {
            let s = Math.max(-1, Math.min(1, input[i]));
            s = s < 0 ? s * 0x8000 : s * 0x7FFF;
            output.setInt16(offset, s, true);
        }
    };

    writeString(0, 'RIFF');
    view.setUint32(4, 36 + samples.length * 2, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true); // PCM
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * numChannels * 2, true);
    view.setUint16(32, numChannels * 2, true);
    view.setUint16(34, 16, true);
    writeString(36, 'data');
    view.setUint32(40, samples.length * 2, true);

    floatTo16BitPCM(view, 44, samples);
    return new Blob([view], { type: 'audio/wav' });
}
